import { VueQueryPlugin, keepPreviousData } from '@tanstack/vue-query';
import type { Plugin } from 'vue';
import type { QueryClient, VueQueryPluginOptions } from '@tanstack/vue-query';
import { isUnexpectedError } from '@/api/errors';
import { retryTransient } from '@/modules/query/utils';

// Ensure that relevant errors are passed to LogRocket
const throwOnError = (err: Error) => isUnexpectedError(err);

export default {
  install(app, queryClient?: QueryClient) {
    if (queryClient) {
      app.use(VueQueryPlugin, { queryClient, enableDevtoolsV6Plugin: true });
      return;
    }

    const vueQueryPluginOptions: VueQueryPluginOptions = {
      enableDevtoolsV6Plugin: true,
      queryClientConfig: {
        defaultOptions: {
          queries: {
            // One retry, as per docs/style-guide/data-fetching-strategy.md, but
            // only for failures another attempt could plausibly fix. A 4xx is the
            // call site's to handle, so retrying it just delays the error the user
            // needs to see.
            ...retryTransient(1),
            staleTime: 0, // All cached data is considered stale. This is a sensible default - see https://tanstack.com/query/latest/docs/framework/vue/guides/important-defaults
            refetchOnMount: true,
            refetchOnWindowFocus: true,
            refetchOnReconnect: true,
            placeholderData: keepPreviousData,
            throwOnError,
          },
          mutations: {
            throwOnError,
          },
        },
      },
    };
    app.use(VueQueryPlugin, vueQueryPluginOptions);
  },
} as Plugin;
